import { colors } from '@smartsheet/lodestar-core';
import { Icon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import { Handle, NodeProps, Position } from 'react-flow-renderer';
import { useDispatch, useSelector } from 'react-redux';
import {
  GraphNodeTypes,
  selectedElementSelector,
} from '../../../../services/ducks/designerDuck/designerDuck';
import { selectExtensionByUUID } from '../../../../services/ducks/extensionsDuck/extensionsDuck';
import { selectModuleById } from '../../../../services/ducks/modulesDuck/modulesDuck';
import { WorkflowStateNode } from '../../../../services/ducks/workflowDuck/workflowDuck';
import { NODE_HEIGHT } from '../../../../utils/dimensions';
import { RemoteImage } from '../../../common/RemoteImage/RemoteImage';
import { DragHandleIcon } from '../../../icons/DragHandleIcon';
import { ModuleIcon } from '../../../icons/ModuleIcon';
import { RemoveNodeButton } from './RemoveNodeButton';
import { IconBox, Label, Node } from './styled';
import { getDragEndHandler, getDragStartHandler, onDragHandler } from './utils';

// A Module has a target handle and a source handle

type ModuleNodeData = {
  data: WorkflowStateNode;
} & {
  data: {
    type: GraphNodeTypes.MODULE | GraphNodeTypes.GROUP_JUNCTION;
  };
};

export const Module = ({
  data,
  forReactFlow = true,
}: NodeProps<ModuleNodeData> & { forReactFlow: boolean }) => {
  const [hover, setHover] = useState<boolean>(false);
  const extension = useSelector(
    selectExtensionByUUID(data.data.pluginUUID ?? '')
  );
  const module = useSelector(
    selectModuleById(data.data.moduleID ?? '', data.data.pluginUUID ?? '')
  );
  const dispatch = useDispatch();

  const logoName = extension.logoName?.split('.')[0] + '.svg';
  const selectedElement = useSelector(selectedElementSelector);

  return (
    <Node
      selected={selectedElement?.id === data.data.id}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      draggable={data.data.type === GraphNodeTypes.MODULE}
      onDragStart={getDragStartHandler(dispatch, data.data.type, {
        __type: 'existing',
        ...data.data,
        type: data.data.type,
      })}
      onDrag={onDragHandler}
      onDragEnd={getDragEndHandler(dispatch)}
    >
      {forReactFlow ? (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      <IconBox color={colors.neutralLight30} hover={hover}>
        {hover ? (
          <Icon svgComponent={DragHandleIcon} size={'xxSmall'} />
        ) : extension.logoName ? (
          <RemoteImage
            url={`https://assets.bridge.smartsheet.com/images/module-logos/${logoName}`}
            style={{ maxHeight: 14, maxWidth: 14 }}
            altText={`${extension.displayName} logo`}
          />
        ) : (
          <Icon svgComponent={ModuleIcon} size={'xxSmall'} />
        )}
      </IconBox>
      <Label selected={selectedElement?.id === data.data.id}>
        {extension.displayName}: {module.name}
      </Label>
      {forReactFlow ? (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={true}
          style={{ visibility: 'hidden', left: NODE_HEIGHT / 2 }}
        />
      ) : null}
      {hover || selectedElement?.id === data.data.id ? (
        <RemoveNodeButton node={data.data} />
      ) : null}
    </Node>
  );
};
